export const OTHER_SPECIALIST_OPTION = 'Other' as const

export const FIXED_SPECIALIST_OPTIONS = [
  'Endocrinology',
  'Gastroenterology',
  'Hematology',
  'Psychiatry',
  'In-person OBGYN',
  'Reproductive Endocrinology',
] as const

/** Pipe, not comma: 1,150 existing rows contain a comma and one contains a pipe. */
export const REFER_TO_SPECIALIST_DELIMITER = ' | '

export type FixedSpecialistOption = (typeof FIXED_SPECIALIST_OPTIONS)[number]

export type SpecialistSelectValue =
  | FixedSpecialistOption
  | typeof OTHER_SPECIALIST_OPTION

export function titleCaseWords(value: string): string {
  const trimmed = value.trim()

  if (trimmed === '') {
    return ''
  }

  return trimmed
    .split(/\s+/)
    .map((word) => {
      if (word.length === 0) {
        return word
      }

      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

export function isFixedSpecialistOption(
  value: string
): value is FixedSpecialistOption {
  return (FIXED_SPECIALIST_OPTIONS as readonly string[]).includes(value)
}

export function parseReferToSpecialist(
  value: string | null | undefined
): string[] {
  if (typeof value !== 'string') {
    return []
  }

  return value
    .split('|')
    .map((part) => part.trim())
    .filter((part) => part !== '')
}

function specialistOrder(value: SpecialistSelectValue): number {
  const index = SPECIALIST_SELECT_OPTIONS.findIndex(
    (option) => option.value === value
  )

  return index === -1 ? SPECIALIST_SELECT_OPTIONS.length : index
}

export function deriveReferToSpecialistValue(params: {
  checked: boolean
  selectedSpecialists: readonly SpecialistSelectValue[]
  otherSpecialistText: string
}): string {
  const { checked, selectedSpecialists, otherSpecialistText } = params

  if (!checked) {
    return ''
  }

  // MultiSelect appends in click order, hydrate rebuilds in option order:
  // without this, reopening a note re-serialises it differently.
  const ordered = [...selectedSpecialists].sort(
    (a, b) => specialistOrder(a) - specialistOrder(b)
  )

  return ordered
    .map((specialist) =>
      specialist === OTHER_SPECIALIST_OPTION
        ? // A pipe in free text would read back as two specialists.
          titleCaseWords(otherSpecialistText).replace(/\|/g, '/')
        : specialist
    )
    .filter((value) => value !== '')
    .join(REFER_TO_SPECIALIST_DELIMITER)
}

export function splitReferToSpecialist(value: string | null | undefined): {
  fixed: FixedSpecialistOption[]
  freeText: string[]
} {
  const fixed: FixedSpecialistOption[] = []
  const freeText: string[] = []

  for (const part of parseReferToSpecialist(value)) {
    if (isFixedSpecialistOption(part)) {
      fixed.push(part)
    } else {
      freeText.push(part)
    }
  }

  return { fixed, freeText }
}

export function hydrateReferToSpecialistSelection(
  referToSpecialist: string | undefined
): {
  selectedSpecialists: SpecialistSelectValue[]
  otherSpecialistText: string
} {
  const selectedSpecialists: SpecialistSelectValue[] = []
  const freeText: string[] = []

  for (const part of parseReferToSpecialist(referToSpecialist)) {
    if (isFixedSpecialistOption(part)) {
      selectedSpecialists.push(part)
    } else {
      freeText.push(part)
    }
  }

  if (freeText.length > 0) {
    selectedSpecialists.push(OTHER_SPECIALIST_OPTION)
  }

  return {
    selectedSpecialists,
    otherSpecialistText: freeText.join(', '),
  }
}

export const FIXED_SPECIALIST_SELECT_OPTIONS = FIXED_SPECIALIST_OPTIONS.map(
  (option) => ({
    label: option,
    value: option,
  })
)

export const SPECIALIST_SELECT_OPTIONS = [
  ...FIXED_SPECIALIST_SELECT_OPTIONS,
  {
    label: OTHER_SPECIALIST_OPTION,
    value: OTHER_SPECIALIST_OPTION,
  },
]
